import React, { useEffect, useState } from "react";
import { getBoardCertifications } from "../APIs/dropdownApi";

export default function BoardCertificationEditModal({
  mode = "add",
  certification = null,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState({
    name: certification?.name || "",
    certificationNumber: certification?.certificationNumber || "",
    issuingBody: certification?.issuingBody || "",
    certifiedOn: certification?.certifiedOn || "",
    expiresOn: certification?.expiresOn || "",
    status: certification?.status || "ACTIVE", // default to ACTIVE
    requiresRenewal: !!certification?.requiresRenewal,
    isVerified: !!certification?.isVerified,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // dropdown state for certifications
  const [options, setOptions] = useState([]);
  const [loadingNames, setLoadingNames] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  // static status options (matches BoardCertificationStatus enum)
  const STATUS_OPTIONS = ["ACTIVE", "EXPIRED", "REVOKED", "PENDING"];

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingNames(true);
      try {
        const list = await getBoardCertifications(); // [{ id, name }]
        if (alive) setOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        if (alive) setLoadErr("Failed to load certifications");
      } finally {
        if (alive) setLoadingNames(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const onSelectCertification = (e) => {
    const val = e.target.value;
    setForm((s) => ({ ...s, name: val || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name || null,
        certificationNumber: form.certificationNumber || null,
        issuingBody: form.issuingBody || null,
        certifiedOn: form.certifiedOn || null,
        expiresOn: form.expiresOn || null,
        status: form.status || null,
        requiresRenewal: !!form.requiresRenewal,
        isVerified: !!form.isVerified,
      };
      await onSave(payload);
    } catch (ex) {
      setErr(ex?.message || "Failed to save board certification");
    } finally {
      setSaving(false);
    }
  };

  // ensure current value shows in dropdown even if it’s not in the list
  const hasCurrentInOptions = !!options.find((o) => o.name === form.name);
  const selectValue = hasCurrentInOptions
    ? form.name
    : form.name
      ? "__custom__"
      : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {mode === "edit"
              ? "Edit Board Certification"
              : "Add Board Certification"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Certification Name Dropdown */}
            <label className="block">
              <span className="text-sm text-gray-700">Certification Name</span>
              <select
                className="mt-1 w-full rounded-md border px-3 py-2"
                value={selectValue}
                onChange={(e) => {
                  if (e.target.value === "__custom__") return;
                  onSelectCertification(e);
                }}
                disabled={loadingNames}
              >
                <option value="">
                  {loadingNames ? "Loading…" : "Select a certification…"}
                </option>
                {!hasCurrentInOptions && form.name && (
                  <option value="__custom__">{form.name} (custom)</option>
                )}
                {options.map((opt) => (
                  <option key={opt.id} value={opt.name}>
                    {opt.name}
                  </option>
                ))}
              </select>
              {loadErr && (
                <div className="mt-1 text-xs text-red-600">{loadErr}</div>
              )}
            </label>

            {/* Certification Number */}
            <label className="block">
              <span className="text-sm text-gray-700">
                Certification Number
              </span>
              <input
                type="text"
                name="certificationNumber"
                value={form.certificationNumber}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. ABX764454"
              />
            </label>

            {/* Issuing Body */}
            <label className="block">
              <span className="text-sm text-gray-700">Issuing Body</span>
              <input
                type="text"
                name="issuingBody"
                value={form.issuingBody}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
                placeholder="e.g. ABPN"
              />
            </label>

            {/* Status Dropdown */}
            <label className="block">
              <span className="text-sm text-gray-700">Status</span>
              <select
                name="status"
                value={form.status}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>

            {/* Certified On */}
            <label className="block">
              <span className="text-sm text-gray-700">Certified On</span>
              <input
                type="date"
                name="certifiedOn"
                value={form.certifiedOn}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
              />
            </label>

            {/* Expires On */}
            <label className="block">
              <span className="text-sm text-gray-700">Expires On</span>
              <input
                type="date"
                name="expiresOn"
                value={form.expiresOn}
                onChange={onChange}
                className="mt-1 w-full rounded-md border px-3 py-2"
              />
            </label>
          </div>

          {/* Checkboxes */}
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                name="requiresRenewal"
                checked={form.requiresRenewal}
                onChange={onChange}
              />
              <span className="text-sm text-gray-700">Requires Renewal</span>
            </label>

            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                name="isVerified"
                checked={form.isVerified}
                onChange={onChange}
              />
              <span className="text-sm text-gray-700">Verified</span>
            </label>
          </div>

          {/* Error + Actions */}
          {err && <p className="text-sm text-red-600">{err}</p>}

          <div className="mt-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded border px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
